type RelativeDatePresetUnit = "days" | "months" | "years";
type RelativeDateDirection = "backward" | "forward";

type RelativeDatePreset = {
	value: string;
	amount: number;
	unit: RelativeDatePresetUnit;
	label: string;
};

const relativeDatePresets = [
	{ value: "30 days", amount: 30, unit: "days", label: "30 days" },
	{ value: "60 days", amount: 60, unit: "days", label: "60 days" },
	{ value: "90 days", amount: 90, unit: "days", label: "90 days" },
	{ value: "120 days", amount: 120, unit: "days", label: "120 days" },
	{ value: "6 months", amount: 6, unit: "months", label: "6 months" },
	{ value: "1 year", amount: 1, unit: "years", label: "1 year" },
	{ value: "2 years", amount: 2, unit: "years", label: "2 years" },
	{ value: "3 years", amount: 3, unit: "years", label: "3 years" },
	{ value: "5 years", amount: 5, unit: "years", label: "5 years" },
] as const satisfies readonly RelativeDatePreset[];

function normalizeRelativeDateValue(value: string) {
	return value.trim().replace(/\s+/g, " ").toLowerCase();
}

function parseRelativeDateValue(value: string) {
	const match = normalizeRelativeDateValue(value).match(/^(\d+)\s+(day|days|month|months|year|years)(?:\s+(forward|forwards|backward|backwards))?$/);
	if (!match) return null;
	const amount = Number(match[1]);
	const singularUnit = match[2].replace(/s$/, "");
	const unit = amount === 1 ? singularUnit : `${singularUnit}s`;
	const rawDirection = match[3];
	const direction: RelativeDateDirection | undefined = rawDirection?.startsWith("forward") ? "forward" : rawDirection?.startsWith("backward") ? "backward" : undefined;
	return { value: `${amount} ${unit}`, direction };
}

function resolveDirection(first?: RelativeDateDirection, second?: RelativeDateDirection) {
	if (first && second && first !== second) return null;
	return first ?? second ?? "backward";
}

function subtractMonths(target: Date, months: number) {
	const day = target.getDate();
	target.setDate(1);
	target.setMonth(target.getMonth() - months);
	const daysInMonth = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
	target.setDate(Math.min(day, daysInMonth));
}

function subtractYears(target: Date, years: number) {
	const day = target.getDate();
	const month = target.getMonth();
	target.setDate(1);
	target.setFullYear(target.getFullYear() - years);
	const daysInMonth = new Date(target.getFullYear(), month + 1, 0).getDate();
	target.setMonth(month);
	target.setDate(Math.min(day, daysInMonth));
}

function inclusiveStartDate(preset: RelativeDatePreset, now: Date) {
	const start = new Date(now);
	if (preset.unit === "days") {
		start.setDate(start.getDate() - preset.amount);
	} else if (preset.unit === "months") {
		subtractMonths(start, preset.amount);
	} else {
		subtractYears(start, preset.amount);
	}

	// The API uses a strict `>` comparison against the relative boundary, so
	// the first eligible filing date is the following calendar day.
	start.setDate(start.getDate() + 1);
	return start;
}

function inclusiveEndDate(preset: RelativeDatePreset, now: Date) {
	const end = new Date(now);
	if (preset.unit === "days") {
		end.setDate(end.getDate() + preset.amount);
	} else if (preset.unit === "months") {
		subtractMonths(end, -preset.amount);
	} else {
		subtractYears(end, -preset.amount);
	}
	return end;
}

function formatShortDate(date: Date) {
	return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
}

export function formatRelativeDatePreset(start: string, end: string, now: Date = new Date()) {
	const parsedStart = parseRelativeDateValue(start);
	const parsedEnd = parseRelativeDateValue(end);
	if (!parsedStart || !parsedEnd) return null;
	const direction = resolveDirection(parsedStart.direction, parsedEnd.direction);
	if (!direction) return null;

	if (direction === "backward" && parsedEnd.value === "1 day") {
		const preset = relativeDatePresets.find((option) => option.value === parsedStart.value);
		if (!preset) return null;
		return `Last ${preset.label} (${formatShortDate(inclusiveStartDate(preset, now))})`;
	}

	if (direction === "forward" && parsedStart.value === "1 day") {
		const preset = relativeDatePresets.find((option) => option.value === parsedEnd.value);
		if (!preset) return null;
		return `Next ${preset.label} (${formatShortDate(inclusiveEndDate(preset, now))})`;
	}

	return null;
}

export function formatRelativeDateFilter(value: unknown, now: Date = new Date()) {
	const raw = String(value ?? "").trim();
	if (!raw) return null;
	const parts = raw.split(/\s*(?:and|,)\s*/i).filter(Boolean);
	const from = parts.find((part) => /^>\s*(?!=)/.test(part))?.replace(/^>\s*/, "");
	const to = parts.find((part) => /^<\s*(?!=)/.test(part))?.replace(/^<\s*/, "");
	if (!from || !to) return null;
	return formatRelativeDatePreset(from, to, now);
}
